import { RootState } from '@/store';
import { ActionContext, ActionTree } from 'vuex';
import { Mutations, MutationTypes } from './mutations';
import { State } from './state';
import ApiService from '@/services/ApiService';

import { ActionTypes as CommonActionTypes } from '../Common/actions';

import {
  Vertrag,
  TextAbschnitt,
  GenerateWordDocumentRequest,
  VertragDokument,
  VertragItem,
  VertragAnalyse,
  FetchVertragAnalysenRequest,
} from '@/models/VertragModels';
import { PagedRequest, PagedResult } from '@/models/CommonModels';

export enum ActionTypes {
  GetVertragById = 'GETVERTRAGBYID',
  GetVertraege = 'GETVERTRAEGE',
  GetVertraegePersonaldaten = 'GETVERTRAEGEPERSONALDATEN',
  GetVertraegePage = 'GETVERTRAEGEPAGE',
  GetAllVertraegeNoLoading = 'GETALLVERTRAEGENOLOADING',
  CreateVertrag = 'CREATEVERTRAG',
  UpdateVertrag = 'UPDATEVERTRAG',
  DeleteVertrag = 'DELETEVERTRAG',

  GenerateWordDocument = 'GENERATEWORDDOCUMENT',

  UpdateAbschnitt = 'UPDATEABSCHNITT',

  GetVertragVertragDokumente = 'GETVERTRAGVERTRAGDOKUMENTE',
  CreateVertragDokument = 'CREATEVERTRAGDOKUMENT',
  UpdateVertragDokument = 'UPDATEVERTRAGDOKUMENT',
  DeleteVertragDokument = 'DELETEVERTRAGDOKUMENT',

  GetVertragAnalysen = 'GETVERTRAGANALYSEN',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload?: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.GetVertragById](context: ActionArguments, id: number): Promise<void>;
  [ActionTypes.GetVertraege](context: ActionArguments): Promise<void>;
  [ActionTypes.GetAllVertraegeNoLoading](context: ActionArguments): Promise<void>;
  [ActionTypes.GetVertraegePersonaldaten](context: ActionArguments, guid: string): Promise<void>;
  [ActionTypes.GetVertraegePage](context: ActionArguments, data: PagedRequest): Promise<void>;
  [ActionTypes.CreateVertrag](context: ActionArguments, data: Vertrag): Promise<number | undefined>;
  [ActionTypes.UpdateVertrag](context: ActionArguments, data: Vertrag): Promise<void>;
  [ActionTypes.DeleteVertrag](context: ActionArguments, vertragid: number): Promise<void>;

  [ActionTypes.GenerateWordDocument](context: ActionArguments, data: GenerateWordDocumentRequest): Promise<void>;

  [ActionTypes.UpdateAbschnitt](context: ActionArguments, data: TextAbschnitt): Promise<void>;

  [ActionTypes.GetVertragVertragDokumente](context: ActionArguments, vertragid: number): Promise<void>;
  [ActionTypes.CreateVertragDokument](context: ActionArguments, data: VertragDokument): Promise<void>;
  [ActionTypes.UpdateVertragDokument](context: ActionArguments, data: VertragDokument): Promise<void>;
  [ActionTypes.DeleteVertragDokument](context: ActionArguments, data: VertragDokument): Promise<void>;

  [ActionTypes.GetVertragAnalysen](context: ActionArguments, data: FetchVertragAnalysenRequest): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  //#region Vertrag
  async [ActionTypes.GetVertragById]({ commit, dispatch, rootGetters }: ActionArguments, id: number) {
    const status = rootGetters.status;
    status.getVertraegeLoading = true;
    status.vertragErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.getVertragById(id)
      .then((res) => {
        const data: Vertrag = res.data;
        commit(MutationTypes.AddOrUpdateVertrag, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.vertragErrorMsg = error.response.data.detail;
        } else {
          status.vertragErrorMsg = 'Es ist ein Fehler aufgetreten';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.getVertraegeLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.GetVertraege]({ commit, dispatch, rootGetters }: ActionArguments) {
    const status = rootGetters.status;
    status.getVertraegeLoading = true;
    status.vertragErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    commit(MutationTypes.SetVertraege, new Array<VertragItem>());

    return ApiService.getAllVertraege()
      .then((res) => {
        const data: Array<VertragItem> = res.data;
        commit(MutationTypes.SetVertraege, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.vertragErrorMsg = error.response.data.detail;
        } else {
          status.vertragErrorMsg = 'Es ist ein Fehler aufgetreten';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.getVertraegeLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.GetAllVertraegeNoLoading]({ commit, dispatch, rootGetters }: ActionArguments) {
    const status = rootGetters.status;
    status.vertragErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    commit(MutationTypes.SetVertraege, new Array<VertragItem>());

    return ApiService.getAllVertraege()
      .then((res) => {
        const data: Array<VertragItem> = res.data;
        commit(MutationTypes.SetVertraege, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.vertragErrorMsg = error.response.data.detail;
        } else {
          status.vertragErrorMsg = 'Es ist ein Fehler aufgetreten';
        }
      })
      .finally(() => {
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.GetVertraegePersonaldaten]({ commit, dispatch, rootGetters }: ActionArguments, guid: string) {
    const status = rootGetters.status;
    status.getVertraegeLoading = true;
    status.vertragErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    commit(MutationTypes.SetVertraege, new Array<VertragItem>());

    return ApiService.getVertragByPersonaldaten(guid)
      .then((res) => {
        const data: Array<VertragItem> = res.data;
        commit(MutationTypes.SetVertraege, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.vertragErrorMsg = error.response.data.detail;
        } else {
          status.vertragErrorMsg = 'Es ist ein Fehler aufgetreten';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.getVertraegeLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.GetVertraegePage]({ commit, dispatch, rootGetters }: ActionArguments, data: PagedRequest) {
    const status = rootGetters.status;
    status.getVertraegeLoading = true;
    status.vertragErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.getVertraegePage(data)
      .then((res) => {
        const data: PagedResult<VertragItem> = res.data;
        commit(MutationTypes.SetVertragPageData, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.vertragErrorMsg = error.response.data.detail;
        } else {
          status.vertragErrorMsg = 'Es ist ein Fehler aufgetreten';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.getVertraegeLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.CreateVertrag]({ commit, dispatch, rootGetters }: ActionArguments, data: Vertrag) {
    const status = rootGetters.status;
    status.createVertragLoading = true;
    status.vertragErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.createVertrag(data)
      .then((res) => {
        const data: Vertrag = res.data;
        commit(MutationTypes.AddVertrag, data);

        return data.id;
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 400) {
          if (error.response.data.errors) {
            for (const key in error.response.data.errors) {
              status.vertragErrorMsg += error.response.data.errors[key] + '\n';
            }
          } else {
            status.vertragErrorMsg = error.response.data.title;
          }
        } else if (error.response.status == 404) {
          status.vertragErrorMsg = error.response.data.detail;
        } else {
          status.vertragErrorMsg = 'Es ist ein unbekannter Fehler aufgetreten.';
        }
        dispatch(CommonActionTypes.SetStatus, status);

        return undefined;
      })
      .finally(() => {
        status.createVertragLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.UpdateVertrag]({ commit, dispatch, rootGetters }: ActionArguments, data: Vertrag) {
    const status = rootGetters.status;
    status.updateVertragLoading = true;
    status.vertragErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.updateVertrag(data)
      .then((res) => {
        const data: Vertrag = res.data;
        commit(MutationTypes.AddOrUpdateVertrag, data);
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 400) {
          status.vertragErrorMsg = error.response.data.title;

          if (error.response.data.errors) {
            for (const key in error.response.data.errors) {
              status.vertragErrorMsg += error.response.data.errors[key] + '\n';
            }
          }
        } else if (error.response.status == 404) {
          status.vertragErrorMsg = error.response.data.detail;
        } else {
          status.vertragErrorMsg = 'Es ist ein unbekannter Fehler aufgetreten.';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.updateVertragLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.DeleteVertrag]({ commit, dispatch, rootGetters }: ActionArguments, vertragid: number) {
    const status = rootGetters.status;
    status.deleteVertragLoading = true;
    status.vertragErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.deleteVertrag(vertragid)
      .then((res) => {
        commit(MutationTypes.DeleteVertrag, vertragid);
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 404) {
          status.vertragErrorMsg = error.response.data.detail;
        } else {
          status.vertragErrorMsg = 'Es ist ein Fehler aufgetreten';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.deleteVertragLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },
  //#endregion

  async [ActionTypes.GenerateWordDocument]({ dispatch, rootGetters }: ActionArguments, data: GenerateWordDocumentRequest) {
    const status = rootGetters.status;
    status.createVertragDokumentLoading = true;
    status.vertragErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.generateWordDocument(data)
      .then((res) => {
        const fileName = res.data;

        return ApiService.getWordDocument(fileName).then((resp) => {
          const url = window.URL.createObjectURL(new Blob([resp.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', data.fileName);
          document.body.appendChild(link);
          link.click();

          // Clean up the URL object
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);
          // return fileName;
        });
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 404) {
          status.vertragErrorMsg = error.response.data.detail;
        } else {
          status.vertragErrorMsg = 'Es ist ein Fehler aufgetreten';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.createVertragDokumentLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.UpdateAbschnitt]({ commit, dispatch, rootGetters }: ActionArguments, data: TextAbschnitt) {
    const status = rootGetters.status;
    status.updateAbschnittLoading = true;
    status.abschnittErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.updateTextabschnitt(data)
      .then((res) => {
        const data: TextAbschnitt = res.data;
        const vertrag = rootGetters.vertraege.find((v: Vertrag) => v.id == data.vertragid);
        const abschnittIndex = vertrag.textabschnitte.findIndex((t: TextAbschnitt) => t.id == data.id);

        vertrag.textabschnitte.splice(abschnittIndex, 1, data);
        commit(MutationTypes.AddOrUpdateVertrag, vertrag);
      })
      .catch((error) => {
        status.abschnittErrorMsg = 'Es ist ein Fehler aufgetreten';
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.updateAbschnittLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  //#region VertragDokument
  async [ActionTypes.GetVertragVertragDokumente]({ commit, dispatch, rootGetters }: ActionArguments, vertragid: number) {
    const status = rootGetters.status;
    status.getVertragDokumenteLoading = true;
    status.vertragDokumentErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.getAllVertragVertragDokumente(vertragid)
      .then((res) => {
        const data: Array<VertragDokument> = res.data;
        if (data != null && data.length > 0) {
          commit(MutationTypes.SetVertragDokumente, data);
        }
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 404) {
          status.vertragDokumentErrorMsg = error.response.data.detail;
        } else {
          status.vertragDokumentErrorMsg = 'Es ist ein Fehler aufgetreten';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.getVertragDokumenteLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.CreateVertragDokument]({ commit, dispatch, rootGetters }: ActionArguments, data: VertragDokument) {
    const status = rootGetters.status;
    status.createVertragDokumentLoading = true;
    status.vertragDokumentErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.createVertragDokument(data)
      .then((res) => {
        const data: VertragDokument = res.data;
        commit(MutationTypes.AddOrUpdateVertragDokument, data);
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 404) {
          status.vertragDokumentErrorMsg = error.response.data.detail;
        } else {
          status.vertragDokumentErrorMsg = 'Es ist ein Fehler aufgetreten';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.createVertragDokumentLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.UpdateVertragDokument]({ commit, dispatch, rootGetters }: ActionArguments, data: VertragDokument) {
    const status = rootGetters.status;
    status.updateVertragDokumentLoading = true;
    status.vertragDokumentErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.updateVertragDokument(data)
      .then((res) => {
        const data: VertragDokument = res.data;
        commit(MutationTypes.AddOrUpdateVertragDokument, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.vertragDokumentErrorMsg = error.response.data.detail;
        } else {
          status.vertragDokumentErrorMsg = 'Es ist ein Fehler aufgetreten';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.updateVertragDokumentLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.DeleteVertragDokument]({ commit, dispatch, rootGetters }: ActionArguments, data: VertragDokument) {
    const status = rootGetters.status;
    status.deleteVertragDokumentLoading = true;
    status.vertragDokumentErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.deleteVertragDokument(data.id)
      .then(() => {
        commit(MutationTypes.DeleteVertragDokument, data);
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 404) {
          status.vertragDokumentErrorMsg = error.response.data.detail;
        } else {
          status.vertragDokumentErrorMsg = 'Es ist ein Fehler aufgetreten';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.deleteVertragDokumentLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },
  //#endregion

  //#region Analysen
  async [ActionTypes.GetVertragAnalysen]({ commit, dispatch, rootGetters }: ActionArguments, data: FetchVertragAnalysenRequest) {
    const status = rootGetters.status;
    status.getVertragAnalysenLoading = true;
    status.vertragAnalysenErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.getVertragAnalysen(data)
      .then((res) => {
        const data: VertragAnalyse = res.data;
        commit(MutationTypes.SetVertragAnalysen, data);
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 404) {
          status.vertragAnalysenErrorMsg = error.response.data.detail;
        } else {
          status.vertragAnalysenErrorMsg = 'Es ist ein Fehler aufgetreten';
        }
      })
      .finally(() => {
        status.getVertragAnalysenLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },
  //#endregion
};
